import { FC, useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/router'
import DOMPurify from 'isomorphic-dompurify'
import clsx from 'clsx'

import ArticleTitle from '../parts/article-title/article-title'
import Commentaries from '../../parts/commentaries/commentaries'
import Recommendations from 'containers/recommendations/recommendations'
import ScrollEventWrap from 'containers/scroll-event-wrap/scroll-event-wrap'
import WidthLimiter from 'containers/width-limiter/width-limiter'
import AudioPlayer from 'components/parts/audio-player/audio-player'
import LectorsBlock from 'components/webinar/webinar-future/webinar-lectors/lectors'
import BreadcrumbsPage from 'components/parts/breadcrumb-page/breadcrumb-page'
import BannerAuthAccess from 'components/banners/banner-auth-access/banner-auth-access'

import { useAuth } from 'store/auth/useAuth'
import { postAxiosSingle } from 'pages/api/axios'
import { preparedIdURL } from 'utils/getURLData'
import { IPodcast } from './article-podcast.type'
import { A_CLINIC_SCROLL } from 'constants/analytic-data'
import { MaterialTypes, PODCAST_URL, URL_SITE } from 'constants/data'
import { URL_LIST } from 'constants/url'
import { originSubscribe, originFWebinar } from 'utils/analytics/webinars/webinar_future'
import { originShare } from 'utils/analytics/other/share_material/share_material'
import { A_LOG_REG_HASH, originRegWebinars } from 'utils/analytics/other/registration/registration'

import styles from './../articles.module.scss'

const ArticlePodcast: FC<IPodcast> = ({
  id,
  name,
  Themes,
  Lecturs,
  Comments,
  audioLink,
  description,
}: IPodcast) => {
  const { asPath } = useRouter()
  const containerRef = useRef<HTMLDivElement>(null)
  const { isAuthorized } = useAuth()
  const [commentsAmount, setCommentsAmount] = useState(0)

  const GAHandler = () => {
    sessionStorage.setItem(A_LOG_REG_HASH, originRegWebinars.LOGIN)
  }

  useEffect(() => {
    const postViews = async () => {
      const idPost = { id: preparedIdURL(window.location.pathname) }
      await postAxiosSingle(URL_LIST.VIEWS_COUNT, {}, idPost)
    }
    postViews()
  }, [asPath])

  return (
    <ScrollEventWrap containerRef={containerRef} event={A_CLINIC_SCROLL}>
      <div className={styles.article}>
        <div className={styles.articleWrapper}>
          <div className={styles.breadcrumb}>
            <BreadcrumbsPage
              breadData={[
                { name: 'Библиотека', link: `${URL_SITE}/library` },
                { name, link: '' },
              ]}
            ></BreadcrumbsPage>
          </div>
          <ArticleTitle
            themes={Themes}
            title={name}
            showLikeShare
            noDateViews
            modificator={styles.header}
            origin={originShare.PODCAST}
          />
          <div className={styles.podcastTop}>
            <WidthLimiter
              noTopPadding
              noBottomPadding
              noLeftPadding
              noRightPadding
              modificator={styles.limiterPodcast}
            >
              {!isAuthorized && (
                <div className={styles.bannerAccess} onClick={GAHandler}>
                  <BannerAuthAccess txt="Чтобы послушать подкаст" />
                </div>
              )}
              <div className={styles.audioplayer}>
                <AudioPlayer
                  src={`${PODCAST_URL}/${id}/${audioLink}`}
                  name={name}
                  materialId={id}
                />
              </div>

              <LectorsBlock Lecturs={Lecturs} />

              <div>
                <h2 className={styles.aboutTitle}>О подкасте</h2>
                <div
                  className={styles.aboutPodcast}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
                ></div>
              </div>

              <div className={styles.commentaries}>
                <Commentaries
                  comments={Comments}
                  materialType={MaterialTypes.PODCAST}
                  id={id}
                  getCommentsAmount={setCommentsAmount}
                />
              </div>
            </WidthLimiter>
            <section
              className={clsx(styles.recomendationSection, styles.recomendationSectionVertical)}
            >
              <WidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
                <div>
                  <h2 className={styles.recommendationsTitle}>Рекомендации</h2>
                  <Recommendations
                    isVertical={true}
                    noMargin={true}
                    noPadding={true}
                    isVerticalMobile
                    AData={{
                      originSub: originSubscribe.PODCAST,
                      originClick: originFWebinar.PODCAST,
                    }}
                    eventWrapperModificator={styles.eventWrapperModificator}
                  />
                </div>
              </WidthLimiter>
            </section>
          </div>
        </div>
      </div>
    </ScrollEventWrap>
  )
}

export default ArticlePodcast
